/**
 * Utility functions for finding tree nodes by various identifiers
 */

import { DataNode } from 'components/WorkspaceEditor/types'

// Configuration for scroll and highlight behavior
export const SCROLL_CONFIG = {
    DELAYS: {
        TREE_EXPANSION: 300,
        SCROLL_WITH_EXPANSION: 500,
        SCROLL_WITHOUT_EXPANSION: 150,
        HIGHLIGHT_DURATION: 700,
        ATTEMPT_SCROLL_WITH_LATEST_CONTENT: 200,
        TAB_SWITCH: 200, // Delay before attempting to scroll after tab change
    },
    TIMEOUTS: {
        REQUEST_FRESHNESS: 10000,
    },
    TARGET_ID_LENGTH: {
        MINIMUM: 10, // Minimum length for a valid target ID
    },
} as const

/**
 * Result of finding a tree node, including path information for expansion
 */
export interface FindNodeResult {
    nodeKey: string
    pathToNode: string[] // Array of keys from root to the target node
    isDirectMatch: boolean // True if content is directly in this node
}

// Sub-function: Checks for direct and prefixed key matches
const checkDirectAndPrefixedKey = (
    node: DataNode,
    targetId: string,
    nodePath: string[]
): FindNodeResult | null => {
    if (node.key === targetId) {
        return { nodeKey: node.key, pathToNode: nodePath, isDirectMatch: true }
    }
    return null
}

// Sub-function: Checks for content item node matches
const checkContentItemNodes = (
    node: DataNode,
    targetId: string,
    nodePath: string[]
): FindNodeResult | null => {
    if (node.contentItemNodes && node.contentItemNodes.length > 0) {
        for (const contentItem of node.contentItemNodes) {
            if (contentItem.id === targetId) {
                return {
                    nodeKey: node.key,
                    pathToNode: nodePath,
                    isDirectMatch: true,
                }
            }
        }
    }
    return null
}

// Sub-function: Checks for rootArticleId match
const checkRootArticleId = (
    node: DataNode,
    targetId: string,
    nodePath: string[]
): FindNodeResult | null => {
    if (node.rootArticleId === targetId) {
        return { nodeKey: node.key, pathToNode: nodePath, isDirectMatch: true }
    }
    return null
}

/**
 * Recursively searches through tree data to find a node by various ID formats
 * @param nodes - Array of tree nodes to search through
 * @param targetId - The ID to search for
 * @param currentPath - Current path of keys from root (used internally)
 * @returns The find result with node key and path, or null if not found
 */
// Main function refactored to use sub-functions
export const findTreeNodeWithPath = (
    nodes: DataNode[],
    targetId: string,
    currentPath: string[] = []
): FindNodeResult | null => {
    for (const node of nodes) {
        const nodePath = [...currentPath, node.key]

        // Use sub-functions for each check
        let result =
            checkDirectAndPrefixedKey(node, targetId, nodePath) ||
            checkContentItemNodes(node, targetId, nodePath) ||
            checkRootArticleId(node, targetId, nodePath)

        if (result) {
            return result
        }

        // Recursively check children
        if (node.children && node.children.length > 0) {
            const childResult = findTreeNodeWithPath(
                node.children,
                targetId,
                nodePath
            )
            if (childResult !== null) {
                return childResult
            }
        }
    }

    return null
}

/**
 * Finds all possible DOM selectors for a given content ID
 * @param contentId - The content ID to find selectors for
 * @returns Array of CSS selectors to try
 */
export const getContentElementSelectors = (contentId: string): string[] => {
    return [
        // Track changes specific selectors
        `[data-suggestion='units-of-measure-${contentId}']`,
        `[data-suggestion='content-suggestion-id-${contentId}']`,
        `[data-testid='plain-text-${contentId}']`,
        `[data-testid='replacement-option-${contentId}']`,
        `[data-testid='option-text-${contentId}']`,
        `[data-testid='option-group-container-${contentId}']`,
        `[data-suggestion-id='${contentId}']`,
    ]
}

/**
 * Attempts to scroll to and highlight a content element
 * @param contentId - The ID of the content to scroll to
 * @param additionalContentIds - Additional content IDs to highlight (optional)
 * @returns Promise that resolves to true if successful, false otherwise
 */
export const scrollToContentElement = async (
    contentId: string,
    additionalContentIds?: string[]
): Promise<boolean> => {
    const selectors = getContentElementSelectors(contentId)
    let scrollSuccess = false

    for (const selector of selectors) {
        try {
            const isOptionGroupContainer = selector.includes(
                'option-group-container'
            )
            const isUOMContainer = selector.includes('units-of-measure')

            // Get the element to scroll to
            const element = document.querySelector(selector) as HTMLElement

            if (element) {
                // Scroll element into view
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                })

                // Highlight based on type
                if (isOptionGroupContainer) {
                    // Don't highlight the container itself, just scroll to it
                    // Individual pills will be highlighted via additionalContentIds
                } else {
                    highlightElement(element, isUOMContainer)
                }

                scrollSuccess = true
                break
            }
        } catch (error) {
            console.warn(`Failed to scroll with selector ${selector}:`, error)
        }
    }

    // Highlight additional content elements if provided
    if (additionalContentIds && additionalContentIds.length > 0) {
        for (const additionalId of additionalContentIds) {
            const additionalSelectors = getContentElementSelectors(additionalId)

            for (const selector of additionalSelectors) {
                try {
                    const isOptionGroupContainer = selector.includes(
                        'option-group-container'
                    )
                    const isUOMContainer = selector.includes('units-of-measure')

                    // Get the specific element by ID
                    const element = document.querySelector(
                        selector
                    ) as HTMLElement

                    if (element) {
                        // Highlight individual elements only, not containers
                        if (!isOptionGroupContainer) {
                            highlightElement(element, isUOMContainer)
                        }
                        break // Found element for this ID, move to next ID
                    }
                } catch (error) {}
            }
        }
    }

    if (!scrollSuccess) {
        console.warn(`No content element found for ID: ${contentId}`)
    }

    return scrollSuccess
}

/**
 * Determines if an element should receive an outline highlight or just focus.
 * Elements that get outline highlights: pills, UOM containers, and plain text within option groups.
 *
 * @param element - The DOM element to check
 * @param isUOMContainer - Whether the element is explicitly a UOM container
 * @returns True if element should get outline highlight, false if just focus
 */
const shouldHighlightWithOutline = (
    element: HTMLElement,
    isUOMContainer?: boolean
): boolean => {
    if (isUOMContainer) return true

    const testId = element.getAttribute('data-testid') || ''

    // Pills (option-text or replacement-option) get outline
    if (
        testId.includes('option-text') ||
        testId.includes('replacement-option')
    ) {
        return true
    }

    // Plain text inside option groups gets outline
    if (
        testId.includes('plain-text') &&
        element.closest('[data-testid*="option-group-container"]')
    ) {
        return true
    }

    return false
}

/**
 * Adds a temporary highlight effect to an element.
 * Elements receive either an outline highlight (pills, UOM, plain text in option groups)
 * or just focus (plain text outside option groups).
 *
 * @param element - The element to highlight
 * @param isUOMContainer - Whether the element is a UOM container
 */
export const highlightElement = (
    element: HTMLElement,
    isUOMContainer?: boolean
): void => {
    if (shouldHighlightWithOutline(element, isUOMContainer)) {
        // Save original styles for restoration
        const originalStyles = {
            transition: element.style.transition,
            outline: element.style.outline,
            outlineOffset: element.style.outlineOffset,
            borderRadius: element.style.borderRadius,
        }

        // Apply outline highlight
        element.style.transition = 'outline 0.3s ease'
        element.style.outline = '2px solid #0D7B55'
        element.style.outlineOffset = '3px'
        element.style.borderRadius = '1px'

        // Restore original styles after duration
        setTimeout(() => {
            element.style.outline = originalStyles.outline
            element.style.outlineOffset = originalStyles.outlineOffset
            element.style.borderRadius = originalStyles.borderRadius
            element.style.transition = originalStyles.transition
        }, SCROLL_CONFIG.DELAYS.HIGHLIGHT_DURATION)
    } else {
        // Plain text outside option groups: just place cursor
        element.focus({ preventScroll: true })
    }
}

/**
 * Checks if a tree node is currently expanded
 * @param nodeKey - The key of the node to check
 * @param expandedKeys - Array of currently expanded keys
 * @returns True if the node is expanded
 */
export const isNodeExpanded = (
    nodeKey: string,
    expandedKeys: React.Key[]
): boolean => {
    return expandedKeys.includes(nodeKey)
}

/**
 * Checks if the tree ref is valid and has the scrollTo method
 * @param treeRef - The tree component ref
 * @returns True if the ref is valid and has scrollTo method
 */
export const isValidTreeRef = (
    treeRef: React.MutableRefObject<any>
): boolean => {
    return !!(treeRef.current && typeof treeRef.current.scrollTo === 'function')
}

/**
 * Expands tree nodes along a path to ensure target content is visible
 * @param expandedKeys - Current expanded keys
 * @param pathToNode - Array of keys representing path to target node
 * @param setExpandedKeys - Function to update expanded keys
 * @returns Promise that resolves when expansion is complete
 */
export const expandTreePath = async (
    expandedKeys: React.Key[],
    pathToNode: string[],
    setExpandedKeys: (keys: React.Key[]) => void
): Promise<void> => {
    return new Promise((resolve) => {
        // Get all parent keys that need to be expanded (exclude the target node itself)
        const keysToExpand = pathToNode.slice(0, -1)

        // Check if any new keys need to be expanded
        const newKeysToExpand = keysToExpand.filter(
            (key) => !expandedKeys.includes(key)
        )

        if (newKeysToExpand.length === 0) {
            // All required nodes are already expanded
            resolve()
            return
        }

        // Expand the required nodes
        const updatedExpandedKeys = [...expandedKeys, ...newKeysToExpand]
        setExpandedKeys(updatedExpandedKeys)
        // Wait for expansion to complete (DOM updates)
        setTimeout(resolve, SCROLL_CONFIG.DELAYS.TREE_EXPANSION)
    })
}

/**
 * Checks if all parent nodes in a path are expanded
 * @param pathToNode - Array of keys representing path to target node
 * @param expandedKeys - Array of currently expanded keys
 * @returns True if all parent nodes are expanded
 */
export const areParentNodesExpanded = (
    pathToNode: string[],
    expandedKeys: React.Key[]
): boolean => {
    const parentKeys = pathToNode.slice(0, -1) // All keys except the target node
    return parentKeys.every((key) => expandedKeys.includes(key))
}
